import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Plus } from "lucide-react";
import { getProduct, getProducts } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { useCart } from "../context/CartContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    setProduct(null); setNotFound(false);
    getProduct(slug)
      .then((p) => {
        setProduct(p);
        getProducts({ category: p.category, limit: 5 }).then((r) =>
          setRelated(r.filter((x) => x.slug !== p.slug).slice(0, 4)));
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <main className="pt-40 pb-40 text-center">
      <p className="text-stone mb-6">Product not found.</p>
      <Link to="/menu" className="text-honey uppercase tracking-widest text-sm">Back to Menu</Link>
    </main>
  );

  if (!product) return <main className="pt-40 pb-40 text-center text-stone">Loading…</main>;

  return (
    <main data-testid="product-detail-page" className="pt-28 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Link to="/menu" className="inline-flex items-center gap-2 text-stone hover:text-honey text-sm uppercase tracking-widest mb-10">
          <ArrowLeft size={16} /> Back to Menu
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <motion.div initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }}
            className="aspect-[4/5] overflow-hidden bg-ink2">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </motion.div>

          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-bone/20 text-stone">{product.strain_type}</span>
              <span className="text-xs uppercase tracking-widest text-honey">{product.brand}</span>
            </div>
            <h1 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tighter leading-[0.9] mb-6">{product.name}</h1>

            <div className="flex items-center gap-8 mb-8 border-y border-bone/10 py-6">
              <div><div className="text-3xl font-display font-bold text-honey">{product.thc}%</div><div className="text-xs uppercase tracking-widest text-stone">THC</div></div>
              <div><div className="text-3xl font-display font-bold text-honey">{product.cbd}%</div><div className="text-xs uppercase tracking-widest text-stone">CBD</div></div>
              <div><div className="text-3xl font-display font-bold text-bone">{product.weight}</div><div className="text-xs uppercase tracking-widest text-stone">Size</div></div>
            </div>

            <p className="text-stone text-lg leading-relaxed mb-6">{product.description}</p>

            <div className="flex flex-wrap gap-2 mb-8">
              {product.effects.map((e) => (
                <span key={e} className="px-4 py-1.5 rounded-full text-xs uppercase tracking-wider bg-ink2 border border-bone/10 text-bone/80">{e}</span>
              ))}
            </div>

            <div className="flex items-center gap-6 mb-8">
              <span className="font-display font-extrabold text-4xl">${product.price.toFixed(2)}</span>
              <button
                data-testid="detail-add-to-cart"
                onClick={() => add(product)}
                className="flex-1 rounded-full bg-honey text-ink px-8 py-4 font-bold tracking-widest uppercase hover:bg-bone transition-colors inline-flex items-center justify-center gap-2"
              >
                <Plus size={18} /> Add to Bag
              </button>
            </div>

            <div className="flex items-center gap-3 text-sm text-stone">
              <ShieldCheck size={18} className="text-honey" /> Lab-tested & pharmacist verified · Reserve online, pick up in-store
            </div>
          </div>
        </div>

        {related.length > 0 && (
          <div className="mt-28">
            <h2 className="font-display font-bold text-3xl tracking-tight mb-10">More {product.category}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
